import { formatDateRu } from '../edit/editCalc';
import { SummaryField, SummaryResponse, SummaryRow } from './summaryApi';

const DATE_FIELD = 'date';
const MIN_COLUMN_WIDTH = 15;
const TOTALS_LABEL = 'Итого:';

/** Число для таблицы на экране: разряды через пробел, до двух знаков после запятой, прочерк для пустого */
export function formatCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '–';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '–';
  return n.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
}

/** Строки отчета в виде, который понимают формы «Касса» и «Банк»: дата ДД.ММ.ГГГГ, остальные поля как есть */
export function toDocumentRows(rows: SummaryRow[]): SummaryRow[] {
  return rows.map((row) => ({ ...row, date: formatDateRu(row.date) }));
}

/** Лист Excel: заголовки, строки периода и строка итогов (массив массивов - подписи колонок могут совпадать) */
export function buildSheetRows(
  fields: SummaryField[],
  rows: SummaryRow[],
  totals: Record<string, number>
): (string | number | null)[][] {
  const header: (string | number | null)[] = fields.map((f) => f.label);
  const body = rows.map((row) =>
    fields.map((f) => {
      if (f.name === DATE_FIELD) return formatDateRu(row.date);
      const v = row[f.name];
      return typeof v === 'number' ? v : null;
    })
  );
  const footer = fields.map((f, i) => {
    if (i === 0) return TOTALS_LABEL;
    return f.summable && f.name in totals ? totals[f.name] : null;
  });
  return [header].concat(body, [footer]);
}

/** Имя файла без символов, недопустимых в Windows */
export function safeFileName(text: string): string {
  return text.replace(/[\\/:*?"<>|]+/g, '_');
}

/** Ширина колонок листа по подписям полей */
export function sheetColumns(fields: SummaryField[]): { wch: number }[] {
  return fields.map((f) => ({ wch: Math.max(f.label.length, MIN_COLUMN_WIDTH) }));
}

async function writeWorkbook(
  fields: SummaryField[],
  rows: SummaryRow[],
  totals: Record<string, number>,
  sheetName: string,
  fileName: string
): Promise<void> {
  // Библиотека тяжелая: подгружается только при выгрузке, а не при открытии вкладки
  const XLSX = await import('xlsx');
  const worksheet = XLSX.utils.aoa_to_sheet(buildSheetRows(fields, rows, totals));
  worksheet['!cols'] = sheetColumns(fields);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, safeFileName(fileName));
}

/** Все колонки сводной таблицы */
export function exportSummaryExcel(report: SummaryResponse): Promise<void> {
  const { pharmacy, startDate, endDate } = report;
  return writeWorkbook(
    report.fields,
    report.rows,
    report.totals,
    'UserData',
    `UserData_${pharmacy.username}_${startDate}_${endDate}.xlsx`
  );
}

/** Выбранные колонки (дата всегда первая) */
export function exportSummaryExcelColumns(report: SummaryResponse, selectedNames: string[]): Promise<void> {
  const { pharmacy, startDate, endDate } = report;
  const fields = report.fields.filter((f) => f.name === DATE_FIELD || selectedNames.indexOf(f.name) >= 0);
  return writeWorkbook(
    fields,
    report.rows,
    report.totals,
    'CustomReport',
    `CustomReport_${pharmacy.username}_${startDate}_${endDate}.xlsx`
  );
}
